import { Fragment, PropsWithChildren } from "react";
import { createStyles, palette } from "../../../../../styles/theme";
import { getConditionnalStyle } from "../../../../../utils/themeUtils";
import { Box } from "../../../Box";
import { DarkTooltip } from "../../../DarkTooltip";
import { isPaleColor } from "../../../../../utils/color.utils";
import { SxProps } from "@mui/material";

const useStyles = (size: number) =>
  createStyles({
    container: {
      borderRadius: "4px",
      cursor: "pointer",
      ">div": {
        borderRadius: "4px",
        minHeight: "100%",
        minWidth: "100%",
        ">div": {
          width: size,
          height: size,
          margin: "1px",
        },
        ">div:first-of-type": {
          borderTopLeftRadius: "4px",
          borderBottomLeftRadius: "4px",
        },
        ">div:last-of-type": {
          borderTopRightRadius: "4px",
          borderBottomRightRadius: "4px",
        },
      },
      img: {
        width: size,
        height: size,
        objectFit: "cover",
        borderRadius: "4px",
      },
    },
    active: {
      "&>div": {
        border: `1px solid ${palette.blue.secondary}`,
      },
      ">div>div": {
        margin: "0",
        border: "1px solid #FFF",
      },
      img: {
        width: size - 2,
        height: size - 2,
      },
    },
    disabled: {
      cursor: "not-allowed",
    },
  });

interface Props extends PropsWithChildren {
  colorOrCombo: string | string[];
  imgSrc?: string;
  isActive?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  size?: number;
  tooltip?: string;
  hidden?: boolean;
  sx?: SxProps;
}

const ColorBox = ({
  colorOrCombo,
  imgSrc,
  isActive,
  disabled,
  onClick,
  size = 20,
  children,
  tooltip,
  hidden,
  sx,
}: Props) => {
  const styles = useStyles(size);
  const colors = Array.isArray(colorOrCombo) ? colorOrCombo : [colorOrCombo];

  const Wrapper = tooltip ? DarkTooltip : Fragment;

  if (disabled || hidden) {
    return null;
  }

  return (
    <Box
      sx={[
        styles.container,
        getConditionnalStyle(isActive, styles.active),
        getConditionnalStyle(disabled, styles.disabled),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      onClick={!disabled ? onClick : undefined}
    >
      <Wrapper {...((tooltip ? { title: tooltip } : {}) as any)}>
        <Box display={"flex"}>
          {colors.map((c) => {
            const needsVisibilityBorder = isPaleColor(c);
            return (
              <Box
                key={c}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: `${100 / colors.length}%`,
                  minHeight: size,
                  background: c,
                  border: needsVisibilityBorder
                    ? `1px solid ${palette.grey.darkGrey}`
                    : undefined,
                  "& img": {
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  },
                }}
              >
                {imgSrc && <img src={imgSrc} alt="color" />}
                {children}
              </Box>
            );
          })}
        </Box>
      </Wrapper>
    </Box>
  );
};

export default ColorBox;
